import * as yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useState } from "react";
import { IoSaveOutline } from "react-icons/io5";
import { FaCircleNotch } from "react-icons/fa";
import { saveAccountAddressInfoAsync } from "../../../../../services/accountService";
import Textbox from "../../../../../components/common/Textbox";
import ReactSelect from "../../../../../components/common/ReactSelect";
import Toaster from "./../../../../../components/common/Toaster";
import locationService from "./../../../../../services/locationService";

const schema = yup.object({
  street: yup.string().required("Please enter street."),
  barangay: yup.string().required("Please enter barangay."),
  zip_code: yup.string().required("Please enter zip code."),
  province: yup.object().shape({
    label: yup.string().required("Please select province."),
    value: yup.string().required("Please select province."),
  }),
  city: yup.object().shape({
    label: yup.string().required("Please select city."),
    value: yup.string().required("Please select city."),
  }),
});

const AddressForm = ({ id, data, isLoading, setLoading, onSuccess }) => {
  const [provinceId, setProvinceId] = useState(null);
  const [provinces, setProvinces] = useState([]);
  const [cities, setCities] = useState([]);

  const {
    register,
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = useForm({ resolver: yupResolver(schema) });

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      await saveAccountAddressInfoAsync(id, data);
      Toaster.Success("Address Saved Successfully");
      onSuccess();
    } catch (ex) {
      Toaster.Error("Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let mounted = true;

    const fetchProvinces = async () => {
      const data = await locationService.getProvincesAsync();
      const options = data.map((item) => {
        const option = {};
        option["label"] = item.province_name;
        option["value"] = item.province_id;

        return option;
      });

      if (mounted) setProvinces(options);
    };

    fetchProvinces();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;

    const fetchCities = async () => {
      const data = await locationService.getCitiesAsync(provinceId);
      const options = data.map((item) => {
        const option = {};
        option["label"] = item.city_name;
        option["value"] = item.city_id;

        return option;
      });

      if (mounted) setCities(options);
    };

    if (provinceId) fetchCities();

    return () => {
      mounted = false;
    };
  }, [provinceId]);

  useEffect(() => {
    const populateForm = () => {
      setValue("address_line_1", data.address_line1);
      setValue("address_line_2", data.address_line2);
      setValue("street", data.street);
      setValue("barangay", data.barangay);
      setValue("zip_code", data.zip_code);

      if (data.province) {
        const selected_province = {
          label: data.province,
          value: data.province_id,
        };

        setValue("province", selected_province);
        setProvinceId(data.province_id);
      }

      // TODO: Update account address API. Add city_id
      if (data.city) {
        const selected_city = {
          label: data.city,
          value: data.city_id,
        };

        setValue("city", selected_city);
      }
    };

    if (data) populateForm();
  }, [data, setValue]);

  const handleProvinceChange = (data) => setProvinceId(data.value);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="p-8 space-y-6">
      <div className="grid grid-cols-2">
        <div className="col-span-2">
          <Textbox
            id="address-line-1-textbox"
            name="address_line_1"
            type="text"
            title="Line 1"
            placeholder="Unit or House No."
            register={register("address_line_1")}
            errors={errors}
            autoFocus
            optional
          />
        </div>
      </div>
      <div className="grid grid-cols-2">
        <div className="col-span-2">
          <Textbox
            id="address-line-2-textbox"
            name="address_line_2"
            type="text"
            title="Line 2"
            placeholder="Building or Subdivision"
            register={register("address_line_2")}
            errors={errors}
            optional
          />
        </div>
      </div>
      <div className="grid grid-cols-2">
        <div className="col-span-2">
          <Textbox
            id="street-textbox"
            name="street"
            type="text"
            title="Street"
            register={register("street")}
            errors={errors}
          />
        </div>
      </div>
      <div className="grid grid-cols-2">
        <div className="col-span-2">
          <Textbox
            id="barangay-textbox"
            name="barangay"
            type="text"
            title="Barangay"
            register={register("barangay")}
            errors={errors}
          />
        </div>
      </div>
      <div className="grid grid-cols-2">
        <div className="col-span-2">
          <ReactSelect
            id="province-dropdown"
            name="province"
            title="Province"
            options={provinces}
            control={control}
            onSelectChange={handleProvinceChange}
            register={register("province")}
            errors={errors}
          />
        </div>
      </div>
      <div className="grid grid-cols-2">
        <div className="col-span-2">
          <ReactSelect
            id="city-dropdown"
            name="city"
            title="City"
            options={cities}
            control={control}
            register={register("city")}
            errors={errors}
          />
        </div>
      </div>
      <div className="grid grid-cols-2">
        <div className="col-span-1">
          <Textbox
            id="zip-code-textbox"
            name="zip_code"
            type="text"
            title="Zip Code"
            register={register("zip_code")}
            errors={errors}
          />
        </div>
      </div>
      <div className="grid grid-2 pt-2">
        <div className="col-span-2">
          <button
            type="submit"
            className="button button-primary disabled:opacity-70 disabled:cursor-not-allowed"
            disabled={isLoading}
          >
            {isLoading && <FaCircleNotch className="animate-spin mr-3" />}
            {!isLoading && <IoSaveOutline className="mr-3" />}
            Save Changes
          </button>
        </div>
      </div>
    </form>
  );
};

export default AddressForm;
